import { useQuery } from "@tanstack/react-query"
import { api } from "../../../lib/api/client"
import { extractArray, extractData } from "../../../lib/api/response-utils"
import { timeEntryTemplatesKeys } from "../../../lib/query-keys"
import type { TimeEntryTemplate } from "../types"

export function useTimeEntryTemplates(division: string | undefined) {
  return useQuery({
    queryKey: timeEntryTemplatesKeys.division(division || ""),
    queryFn: async () => {
      if (!division) throw new Error("Division is required")
      const response = await api.timeEntryTemplates.find(division)
      return extractArray<TimeEntryTemplate>(response)
    },
    enabled: !!division,
  })
}

export function useTimeEntryTemplate(id: string | undefined) {
  return useQuery({
    queryKey: timeEntryTemplatesKeys.detail(id || ""),
    queryFn: async () => {
      if (!id) throw new Error("ID is required")
      const response = await api.timeEntryTemplates.findById(id)
      return extractData<TimeEntryTemplate>(response)
    },
    enabled: !!id,
  })
}

/**
 * Returns templates filtered by billing category.
 * Uses the full division list and filters client-side since
 * the number of templates per division is typically small.
 */
export function useTemplatesByCategory(
  division: string | undefined,
  billingCategoryId: string | undefined
) {
  const query = useTimeEntryTemplates(division)

  return {
    ...query,
    data: query.data?.filter((t) => t.billing_category === billingCategoryId) ?? [],
  }
}
